/**
 * Prepare an identity document for upload.
 *
 * Photos are re-encoded as JPEG on the device: phone cameras produce 3–8 MB
 * images that would blow the upload limit and eat mobile data, and re-encoding
 * also drops the EXIF block — which on a phone photo includes GPS coordinates
 * of where it was taken. 1800px on the long edge keeps an Aadhaar card's small
 * print legible; if that's still too big we step down.
 *
 * PDFs (an e-Aadhaar, a downloaded bill) are sent as they are.
 */

// Must match MAX_DOCUMENT_BYTES on the API.
export const MAX_UPLOAD_BYTES = 1_400_000;

function readAsDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Could not read that file.'));
    reader.readAsDataURL(blob);
  });
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('That image couldn’t be opened. Try a JPEG or PNG photo.'));
    };
    img.src = url;
  });
}

const dataUrlBytes = (dataUrl) => Math.floor(((dataUrl.length - dataUrl.indexOf(',') - 1) * 3) / 4);

export async function fileToUploadDataUrl(file) {
  if (!file) throw new Error('Choose a file first.');

  if (file.type === 'application/pdf' || /\.pdf$/i.test(file.name ?? '')) {
    if (file.size > MAX_UPLOAD_BYTES) {
      throw new Error('That PDF is over 1.4 MB. Take a photo of the document instead.');
    }
    return readAsDataUrl(file);
  }

  if (file.type && !file.type.startsWith('image/')) {
    throw new Error('Choose a photo or a PDF.');
  }

  const img = await loadImage(file);
  for (const [max, quality] of [[1800, 0.85], [1400, 0.75], [1100, 0.65]]) {
    const scale = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
    const w = Math.max(1, Math.round(img.naturalWidth * scale));
    const h = Math.max(1, Math.round(img.naturalHeight * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    // White behind transparent PNGs, which would otherwise encode as black.
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    const dataUrl = canvas.toDataURL('image/jpeg', quality);
    if (dataUrlBytes(dataUrl) <= MAX_UPLOAD_BYTES) return dataUrl;
  }
  throw new Error('That photo is too large even after compressing. Try a closer, simpler shot.');
}
